import { joinAdjacentLists, joinBlocks, spliceBlocks, setBlockProps, takeInlineTail } from './ops'
import type { Blocks } from '@w/content-blocks/src/block'
import type { EditorIntent } from './intents'
import { lift } from './structure'
import type { CoreState } from './state'
import { children, locate, plain, pointAt, rows, type Element, type Row } from './tree'

type Kind = Extract<EditorIntent, { type: 'formatBlock' }>['block']

/** List kinds convert a list's subtype in place; a toggle list is a list of subtype `toggle` (Notion turn into). */
const listSubtypes: Partial<Record<Kind, 'ordered' | 'todo' | 'toggle' | undefined>> = { bullet: undefined, number: 'ordered', check: 'todo', toggle: 'toggle' }
const isListKind = (kind: Kind) => kind in listSubtypes

export function formatBlockRange(
  state: CoreState, entries: Row[], start: { row: number; offset: number },
  end: { row: number; offset: number }, kind: Kind,
): void {
  const selection = state.selection
  const converted = new Set<string>()
  const last = !selection?.nodes?.length && end.row > start.row && end.offset === 0 ? end.row - 1 : end.row
  const targets = entries.slice(start.row, last + 1).map((row, index) => ({
    id: row.block.id,
    from: selection?.nodes?.length ? 0 : index === 0 ? start.offset : 0,
    to: !selection?.nodes?.length && start.row + index === end.row ? end.offset : plain(row.block).length,
  }))
  const selectedIds = new Set(targets.map((target) => target.id))
  const handled = new Set<string>()
  const listKind = isListKind(kind)
  const subtype = listSubtypes[kind]
  const firstList = entries[start.row].parents.findLast((parent) => parent.type === 'list')
  const toggleOff = listKind && firstList !== undefined && firstList.subtype === subtype
  for (const target of targets) {
    if (handled.has(target.id)) continue
    let row = rows(state.blocks).find((entry) => entry.block.id === target.id)
    if (!row) continue
    const text = plain(row.block)
    // A range turns the soft lines it touches; code takes the whole block (superseded 2026-10-30 by Notion browser
    // check: before `text "l\nm\nn"`, selection `"m"`; after `notion-code-block "l\nm\nn\n"`).
    const whole = kind === 'code'
    const from = whole || target.from === 0 ? 0 : text.lastIndexOf('\n', target.from - 1) + 1
    const boundary = whole ? -1 : text.indexOf('\n', Math.max(target.from, target.to - 1))
    const to = boundary === -1 ? text.length : boundary
    const list = row.parents.findLast((parent) => parent.type === 'list')
    const targetKind = toggleOff ? 'paragraph' : kind
    if (listKind && !toggleOff && list?.subtype === subtype && list) {
      converted.add(list.id)
      continue
    }
    if (list && listKind && targetKind !== 'paragraph' && from === 0 && to === text.length &&
      rows([list]).every((entry) => selectedIds.has(entry.block.id))) {
      convertList(state, list, subtype)
      for (const entry of rows([list])) handled.add(entry.block.id)
      selectEnd(state, list)
      continue
    }
    // A partly selected list's selected item takes the kind alone: its list splits around it, in place.
    const item = row.parents.at(-1)
    if (list && listKind && !toggleOff && item?.type === 'li' && item.children[0] === row.block && row.parents.at(-2) === list) {
      converted.add(ownList(state, row, item, list, subtype).id)
      selectEnd(state, row.block)
      continue
    }
    const { siblings } = row
    const original = row.block
    const block = structuredClone(original)
    const parts: Blocks = [block]
    if (to < text.length) {
      const tail = takeInlineTail(state, block.children, to + 1)
      takeInlineTail(state, block.children, to)
      parts.push({ ...block, id: '', children: tail } as Element)
    }
    if (from > 0) {
      const selected = takeInlineTail(state, block.children, from)
      takeInlineTail(state, block.children, from - 1)
      const id = ''
      parts.splice(1, 0, { ...block, id, children: selected } as Element)
    }
    if (parts.length > 1) {
      spliceBlocks(state, siblings, siblings.indexOf(original), 1, ...parts)
      const id = parts[from > 0 ? 1 : 0].id
      row = rows(state.blocks).find((entry) => entry.block.id === id)
      if (!row) throw new Error('Selected split block is missing')
    }
    // Each row converts in its own block list (formatBlock); converted rows of one list merge below.
    const converting: string = row.block.id
    formatBlock(state, row, targetKind, false)
    const result = rows(state.blocks).find((entry) => entry.block.id === converting ||
      entry.parents.some((parent) => parent.id === converting))
    converted.add(result?.parents.findLast((parent) => parent.type === 'list' || parent.type === 'quote')?.id ?? converting)
  }
  if (listKind) {
    const mergeLists = (items: Blocks) => {
      for (let index = 1; index < items.length; index += 1) {
        const previous = items[index - 1]
        const current = items[index]
        if (previous.type !== 'list' || current.type !== 'list' ||
          previous.subtype !== current.subtype ||
          (!converted.has(previous.id) && !converted.has(current.id))) continue
        converted.add(previous.id)
        joinBlocks(state, previous, current)
        index -= 1
      }
      for (const item of items) mergeLists(children(item))
    }
    mergeLists(state.blocks)
  }
  if (kind === 'quote' && !selection?.nodes?.length) {
    const merge = (items: Blocks) => {
      for (let index = 1; index < items.length; index++) {
        const previous = items[index - 1]
        const current = items[index]
        if (previous.type !== 'quote' || current.type !== 'quote' ||
          !converted.has(previous.id) || !converted.has(current.id)) continue
        const moved = spliceBlocks(state, current.children, 0, current.children.length)
        spliceBlocks(state, previous.children, previous.children.length, 0, ...moved)
        spliceBlocks(state, items, index, 1)
        index--
      }
      for (const item of items) merge(children(item))
    }
    merge(state.blocks)
  }
  if (selection) {
    const ids = new Set<string>()
    const collect = (items: Blocks) => {
      for (const item of items) {
        ids.add(item.id)
        collect(children(item))
      }
    }
    collect(state.blocks)
    if (ids.has(selection.anchor.blockId) && ids.has(selection.focus.blockId)) {
      state.selection = selection
    }
  }
}

type ListBlock = Extract<Element, { type: 'list' }>
type ItemBlock = Extract<Element, { type: 'li' }>

/** List item `item` of `list` (row `row` is its own row) leaves into a list of `subtype` of its own, `list` splitting
 * around it in place. `list` keeps the items after `item` (else those before), so a peer's concurrent item added to
 * them stays in a list that still exists. */
function ownList(state: CoreState, row: Row, item: ItemBlock, list: ListBlock, subtype: ListBlock['subtype']): ListBlock {
  const at = list.children.indexOf(item)
  const keepsTail = at < list.children.length - 1
  spliceBlocks(state, list.children, at, 1)
  const outer = row.parents.at(-3)?.children ?? state.blocks
  // The moved item takes its new list's checkbox state with it (a to-do list's items carry `done`).
  const moved: ItemBlock = { ...item, done: subtype === 'todo' ? item.done ?? false : undefined }
  const own: ListBlock = { id: '', type: 'list', ...(subtype ? { subtype } : {}), children: [moved] }
  if (keepsTail) {
    const before = spliceBlocks(state, list.children, 0, at)
    spliceBlocks(state, outer, outer.indexOf(list), 0,
      ...(before.length > 0 ? [{ ...list, id: '', children: before }] : []), own)
    // The list before takes the start number; the kept tail numbers from 1 again (Notion run 2: the item after restarts).
    if (before.length > 0 && list.start !== undefined) setBlockProps(state, list, { start: undefined })
  } else {
    spliceBlocks(state, outer, outer.indexOf(list) + 1, 0, own)
    if (list.children.length === 0) spliceBlocks(state, outer, outer.indexOf(list), 1)
  }
  return own
}

/**
 * Turns the caret's row into block kind `kind` in its own block list (INV-LEVEL ownership, as placement.ts places
 * blocks): a row in a quote, callout, column, toggle or list item stays there. A list item's own row is the item: its
 * list's kind converts the whole list, and the list's kind again (or a paragraph over its paragraph) ends the item one
 * level, in place (`lift`). A row already of the kind (a quote's row asked for a quote) is left as is.
 */
export function formatBlock(state: CoreState, row: Row, kind: Kind, joinNeighbours = true): void {
  // The caret keeps its offset in the converted row (Notion turn into; ProseMirror setBlockType maps the selection).
  const caret = caretOffset(state, row)
  const listKind = isListKind(kind)
  const subtype = listSubtypes[kind]
  const parent = row.parents.at(-1)
  const list = row.parents.at(-2)
  const item = parent?.type === 'li' && parent.children[0] === row.block && list?.type === 'list' && list.subtype !== 'toggle'
    ? parent : undefined
  if (item && list?.type === 'list' && listKind && list.subtype !== subtype && list.children.length > 1) {
    // The caret's item alone takes the kind (superseded 2026-10-30 by Notion browser check, Cmd+Opt+5 on item 2 of 1..3:
    // `[numbered,"e","1."],[bulleted,"f","•"],[numbered,"g","1."]`). Compatible neighbours join through the CRDT.
    const own = ownList(state, row, item, list, subtype)
    if (joinNeighbours) joinAdjacentLists(state, own, row.parents.at(-3)?.children ?? state.blocks)
    selectAt(state, row.block, caret)
    return
  }
  if (item && list?.type === 'list' && listKind && list.subtype !== subtype) {
    // The kind and the checkbox state change on the same list: a peer's concurrent items stay in it.
    setBlockProps(state, list, { subtype })
    for (const entry of list.children) {
      if (entry.type !== 'li') continue
      setBlockProps(state, entry, { done: subtype === 'todo' ? entry.done ?? false : undefined })
    }
    if (joinNeighbours) joinAdjacentLists(state, list, row.parents.at(-3)?.children ?? state.blocks)
    selectAt(state, row.block, caret)
    return
  }
  if (item && (listKind || (kind === 'paragraph' && row.block.type === 'p'))) {
    const id = row.block.id
    const sub = item.children.at(-1)
    lift(state, row, item)
    const lifted = rows(state.blocks).find((entry) => entry.block.id === id)
    // The item's sub-list outdents into the rest of its list (Lexical): it joins the list after it.
    const at = lifted && sub?.type === 'list' ? lifted.siblings.indexOf(sub) : -1
    const next = at === -1 ? undefined : lifted?.siblings[at + 1]
    if (lifted && sub?.type === 'list' && next?.type === 'list' && next.subtype === sub.subtype) {
      joinBlocks(state, next, sub, 0)
    }
    if (lifted) selectAt(state, lifted.block, caret)
    return
  }
  const target = row.block
  // A block already of the requested kind is left as is: rewriting it would author CRDT operations that change nothing.
  if ((kind === 'paragraph' && target.type === 'p') || (kind === 'code' && target.type === 'pre') ||
    (target.type === 'h' && `h${target.level}` === kind) || (kind === 'quote' && parent?.type === 'quote') ||
    (kind === 'callout' && parent?.type === 'alert')) {
    selectAt(state, target, caret)
    return
  }
  const next = turnInto(target, kind)
  spliceBlocks(state, row.siblings, row.siblings.indexOf(target), 1, next)
  selectAt(state, next, caret)
  if (joinNeighbours && next.type === 'list') joinAdjacentLists(state, next, row.siblings)
}

/** Block `target` as block kind `kind`, keeping its identity: its rows' inline content joined by line breaks, or each
 * row a list item for a list kind; a list keeps its items and takes the kind. */
export function turnInto(target: Element, kind: Kind): Element {
  const subtype = listSubtypes[kind]
  if (isListKind(kind) && target.type === 'list') {
    const { subtype: _previous, ...rest } = target as Element & { subtype?: string }
    return { ...rest, ...(subtype ? { subtype } : {}), children: target.children.map((item) =>
      item.type === 'li' ? { ...item, done: subtype === 'todo' ? item.done ?? false : undefined } : item) } as Element
  }
  const content = inlineContent(target)
  let next: Element
  if (kind === 'bullet' || kind === 'number' || kind === 'check') {
    const items = rows([target]).map((entry) => {
      const item: Blocks[number] = { id: '', type: 'li',
        children: [{ id: '', type: 'p', children: entry.block.children }],
      }
      if (kind === 'check') item.done = false
      return item
    })
    next = { id: target.id, type: 'list', ...(subtype ? { subtype } : {}), children: items }
  } else if (kind === 'quote') {
    next = { id: target.id, type: 'quote',
      children: [{ id: '', type: 'p', children: content }] }
  } else if (kind === 'callout') {
    // Notion turn into callout: the block's text becomes the callout's paragraph (slash-menu Callout's icon and color).
    next = { id: target.id, type: 'alert', icon: '💡', color: 'info',
      children: [{ id: '', type: 'p', children: content }] }
  } else if (kind === 'toggle') {
    // Notion turn into toggle list: the block's text becomes the toggle's header.
    next = { id: target.id, type: 'list', subtype: 'toggle',
      children: [{ id: '', type: 'li', children: [{ id: '', type: 'p', children: content }] }] }
  } else if (kind === 'code') next = { id: target.id, type: 'pre', children: content }
  else if (kind === 'paragraph') next = { id: target.id, type: 'p', children: content }
  else next = { id: target.id, type: 'h', level: Number(kind.slice(1)) as 1 | 2 | 3,
    children: content }
  return next
}

function inlineContent(block: Element): Blocks {
  if (['p', 'h', 'pre', 'code', 'td'].includes(block.type)) return block.children
  const result: Blocks = []
  for (const row of rows([block])) {
    if (result.length > 0) result.push({ id: '', type: 'br' })
    result.push(...row.block.children)
  }
  return result
}

/** The collapsed caret's offset in `row`, else undefined. */
function caretOffset(state: CoreState, row: Row): number | undefined {
  const selection = state.selection
  if (!selection || selection.nodes?.length || selection.table) return undefined
  const { anchor, focus } = selection
  if (anchor.blockId !== focus.blockId || anchor.offset !== focus.offset) return undefined
  const entries = rows(state.blocks)
  const at = locate(entries, focus)
  return entries[at.row]?.block.id === row.block.id ? at.offset : undefined
}

/** The caret at `offset` in the first row of `block` when it has one row, else at its end. */
function selectAt(state: CoreState, block: Element, offset: number | undefined): void {
  const all = rows([block])
  if (offset === undefined || all.length !== 1) {
    selectEnd(state, block)
    return
  }
  const point = pointAt(all[0], Math.min(offset, plain(all[0].block).length))
  state.selection = { anchor: point, focus: point }
}

function selectEnd(state: CoreState, block: Element): void {
  const row = rows([block]).at(-1)
  if (!row) return
  const point = pointAt(row, plain(row.block).length)
  state.selection = { anchor: point, focus: point }
}

function convertList(
  state: CoreState, container: Element, subtype: 'ordered' | 'todo' | 'toggle' | undefined,
): void {
  if (container.type === 'list') {
    setBlockProps(state, container, { subtype })
    for (const item of container.children) {
      if (item.type === 'li') {
        setBlockProps(state, item, { done: subtype === 'todo' ? item.done ?? false : undefined })
      }
    }
  }
  for (const child of container.children) {
    if ('children' in child) convertList(state, child as Element, subtype)
  }
}
